<template>
  <div
    class="academic-progress-level-picker"
    :class="[
      `academic-progress-level-picker--${variant}`,
      { 'is-disabled': disabled, 'is-invalid': invalid },
    ]"
  >
    <div
      class="level-select"
      :class="{ 'cursor-pointer': !disabled }"
      :style="selectStyle"
      @click="!disabled && (isOpen = !isOpen)"
    >
      <span
        v-if="selectedLevel && variant === 'outline'"
        class="level-select__dot"
        :style="{ backgroundColor: selectedLevel.color }"
      />
      <span class="level-select__text">{{ selectedLevel ? selectedLevel.name : placeholder }}</span>
      <i class="far fa-angle-down level-select__caret" />
    </div>

    <div
      v-if="isOpen && !disabled"
      class="level-option-container position-absolute d-flex flex-column justify-content-center py-2 px-1 bg-white rounded shadow"
    >
      <div v-for="level in levels" :key="`level-${level.id}`" class="mx-1">
        <div
          class="cursor-pointer px-3 my-1 text-center rounded font-weight-bold"
          :style="{ backgroundColor: level.color }"
          @click="selectLevel(level)"
        >
          {{ level.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AcademicProgressLevelPicker',
  props: {
    value: {
      type: Number,
      default: null,
    },
    levels: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Marca el control en rojo (borde + texto), como cualquier campo
    // requerido sin completar en un formulario. Pensado para el estado
    // "se intentó guardar y falta elegir un nivel".
    invalid: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Nivel',
    },
    variant: {
      type: String,
      default: 'outline',
      validator: (value) => ['outline', 'solid'].includes(value),
    },
    // Alto del control, en cualquier unidad CSS válida. Se expone como prop
    // (en vez de resolverlo con flexbox/stretch contra el elemento vecino)
    // porque el picker vive en un contenedor ajeno: quien lo usa conoce el
    // alto exacto que necesita matchear (por ejemplo, el de los radios de
    // Fortaleza/Necesidad) y lo pasa explícito.
    height: {
      type: String,
      default: '5.5rem',
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    selectedLevel() {
      return this.levels.find((level) => level.id === this.value) || null;
    },
    selectStyle() {
      const style = { height: this.height };

      if (!this.selectedLevel) return style;

      return this.variant === 'solid'
        ? { ...style, backgroundColor: this.selectedLevel.color, borderColor: this.selectedLevel.color }
        : { ...style, borderColor: this.selectedLevel.color };
    },
  },
  methods: {
    selectLevel(level) {
      this.$emit('input', level.id);
      this.isOpen = false;
    },
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },
  },
  mounted() {
    // El popup usa position:absolute, así que su área visual queda fuera
    // del bounding box del wrapper (que se ajusta solo al tamaño del
    // control): un mouseleave del wrapper se dispara apenas el mouse baja
    // hacia el popup, cerrándolo antes de poder hacer click en una opción.
    // Por eso se cierra con click afuera, no con mouseleave.
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
};
</script>

<style scoped>
.academic-progress-level-picker {
  position: relative;
  display: inline-flex;
}
.cursor-pointer {
  cursor: pointer;
}

/* Control en sí: mismo lenguaje visual que un <select> de formulario, para
   que se note que es un dropdown incluso sin ningún valor elegido. El alto
   viene de la prop `height` (bindeado en selectStyle), no hardcodeado acá. */
.level-select {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 100px;
  padding: 0 10px;
  border: 1px solid #ced4da;
  border-radius: 5px;
  background-color: #ffffff;
  color: #495057;
  font-weight: 600;
  font-size: 1rem;
  box-sizing: border-box;
}
.academic-progress-level-picker--solid .level-select {
  color: #212529;
}
.is-disabled .level-select {
  background-color: #f2f2f2;
  color: #adb5bd;
  border-color: #e9ecef;
}
/* Mismo rojo que usa el componente Error compartido, para que el estado
   inválido se sienta consistente con el resto de los mensajes de error. */
.is-invalid .level-select {
  border-color: #ff5b5c;
  color: #ff5b5c;
}
.is-invalid .level-select__caret {
  color: #ff5b5c;
}

.level-select__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.level-select__text {
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.level-select__caret {
  color: #6c757d;
  flex-shrink: 0;
}
.is-disabled .level-select__caret {
  color: #ced4da;
}

.level-option-container {
  top: 100%;
  z-index: 10;
  min-width: 99px;
  margin-top: 2px;
}
</style>
